// @flow
import map from 'lodash/fp/map';
import identity from 'lodash/fp/identity';

import { stringAtStartOfISODay } from '../date';
import * as note from '../product-note';
import * as label from '../return-label';
import { deserialized as deserializedBarcode } from '../barcode';
import {
  deserializedQuikContactListResponse,
  deserializedQuikOrderListResponse,
  deserializedQuikPendingOrderListResponse,
  deserializedQuikOrderDetailsResponse,
  deserializedQuikShipmentListResponse,
  deserializedQuikOpenItemsListResponse,
  deserializedQuikPaymentListResponse,
  deserializedQuikPaymentDetailsResponse,
  deserializedQuikShipmentDetailsResponse,
} from '../quik-order-history';
import presignedPostSchema from './schemas/presigned-post.json';
// import variationsQuerySchema from './schemas/variations-query.json';
// types
import {
  type SerializedReportOrders,
  type ReportOrdersFormValues,
  serializeReportOrders,
  deserializeReportOrders,
} from '../report-orders';
import type { PresignedPost } from './response-types/presigned-post';
import type { ProductNote, SerializedProductNote } from '../product-note';
import type { Scan } from '../scan';
import type { BarcodeQuery, SerializedBarcodeQuery } from '../barcode';
import type {
  ExternalPricesQuery,
  SerializedExternalPricesQuery,
} from '../external-prices';
import type {
  QuikContactListQuery,
  SerializedQuikContactListQuery,
  QuikOpenItemsListQuery,
  SerializedQuikOpenItemsListQuery,
  QuikPaymentListQuery,
  SerializedQuikPaymentListQuery,
  QuikOrderListQuery,
  SerializedQuikOrderListQuery,
  QuikShipmentListQuery,
  SerializedQuikShipmentListQuery,
  QuikOrderDetailsQuery,
  SerializedQuikOrderDetailsQuery,
  QuikPaymentDetailsQuery,
  SerializedQuikPaymentDetailsQuery,
  QuikShipmentDetailsQuery,
  SerializedQuikShipmentDetailsQuery,
  SerializedQuikPendingOrderListResponse,
  QuikPendingOrder,
} from '../quik-order-history';
import {
  type ProductsQueryResponse,
  type SerializedProductsQueryResponse,
  type VariationsQueryResponse,
  type SerializedVariationsQueryResponse,
  type ObjectMovieQueryResponse,
  deserializedProductsQueryResponse,
  deserializedVariationsQueryResponse,
  productsQueryAsQueryString,
} from '../product';
import type { ReturnLabel, SerializedReturnLabel } from '../return-label';
import type { OrdersQueryResponse } from '../order';

import {
  QUIK_CONTACT_LIST,
  QUIK_OPEN_ITEMS_LIST,
  QUIK_ORDER_DETAILS,
  QUIK_ORDER_LIST,
  QUIK_PENDING_ORDER_LIST,
  QUIK_PAYMENT_DETAILS,
  QUIK_PAYMENT_LIST,
  QUIK_SHIPMENT_DETAILS,
  QUIK_SHIPMENT_LIST,
} from 'actions/action-types/quik-order-history';
import {
  type CustomersQueryResponse,
  type SerializedCustomersQueryResponse,
  type CustomerTagTreeResponse,
  customersQueryAsQueryString,
  deserializedCustomersQueryResponse,
} from 'modules/customer';
import {
  type UsersQueryResponse,
  type SerializedUsersQueryResponse,
  type ManagedUser,
  type SerializedManagedUser,
  usersQueryAsQueryString,
  deserializedUsersQueryResponse,
  serializedManagedUser,
  deserializedManagedUser,
  deserializedManagedUsers,
} from 'modules/user';
import {
  type OrderHistoryResponse,
  type SerializedOrderHistoryResponse,
  type OrderHistoryQuery,
  orderHistoryQueryAsString,
  deserializedOrderHistoryResponse,
} from 'modules/order-history';
import {
  variationsForCampaignsQueryAsQueryString,
  type CampaignsQueryResponse,
  type VariationsQueryResponseForCampaigns,
  type SerializedCampaignsQueryResponse,
  type CampaignsQuery,
  campaignsQueryAsQueryString,
  deserializedCampaignsQueryResponse,
  type SerializedCampaignDraft,
  type CampaignDraft,
  serializedCampaignDraft,
  serializedEditedCampaign,
  type ContactsQueryResponse,
  type SerializedContactsQueryResponse,
  type ContactsQuery,
  contactsQueryAsQueryString,
  deserializedContactsQueryResponse,
  type GroupsQueryResponse,
  type SerializedGroupsQueryResponse,
  type GroupsQuery,
  groupsQueryAsQueryString,
  deserializedGroupsQueryResponse,
  type CampaignRecipientsQueryResponse,
  type SerializedCampaignRecipientsQueryResponse,
  type CampaignRecipientsQuery,
  campaignRecipientsQueryQueryAsQueryString,
  deserializedCampaignRecipientsQueryResponse,
  type GroupQueryResponse,
  type DocumentsQuery,
  documentsQueryAsQueryString,
  type CampaignSendTestEmailResponse,
  type SerializedCampaignSendTestEmailResponse,
  type CampaignSendTestEmailQuery,
  campaignSendTestEmailQueryAsQueryString,
} from 'modules/campaign';

/* 💁 A Resource is intended to map to the corresponding rails resource.

Every Resource needs serialization and deserialization methods, since the
backend naming conventions do not match the frontend (maybe they should 🤔?)–the
backend uses snake_case, the frontend camelCase.

Resources are sent over the wire serialized as JSON, but when they are
manipulated/used on the frontend (aka this code base), it is *always*
in their deserialized form.

Each resource has unique name that lets reducers know what type of resource they
are dealing with in data fetching actions.

A Resource path is either a string corresponding to the backend endpoint, or a function
that takes the id of the resource as its only argument and returns the appropriate endpoint.

How do we indicate that a resource only has certain actions available?
PresignedPost, for example, is a create-only resource.

*/

/* 💁 The main reason for making these constants is to avoid typos. */
const MANAGED_USER: 'MANAGED_USER' = 'MANAGED_USER';
const MANAGED_USERS: 'MANAGED_USERS' = 'MANAGED_USERS';
const USER: 'USER' = 'USER';
const PRESIGNED_POSTS: 'PRESIGNED_POSTS' = 'PRESIGNED_POSTS';
const PRODUCTS: 'PRODUCTS' = 'PRODUCTS';
const PRODUCT_NOTE: 'PRODUCT_NOTE' = 'PRODUCT_NOTE';
const PRODUCT_NOTES: 'PRODUCT_NOTES' = 'PRODUCT_NOTES';
const PRODUCTS_FOR_SCAN: 'PRODUCTS_FOR_SCAN' = 'PRODUCTS_FOR_SCAN';
const BARCODES_FOR_SCAN: 'BARCODES_FOR_SCAN' = 'BARCODES_FOR_SCAN';
const EXTERNAL_PRICES: 'EXTERNAL_PRICES' = 'EXTERNAL_PRICES';
const RETURN_LABEL: 'RETURN_LABEL' = 'RETURN_LABEL';
const REPORT_ORDERS: 'REPORT_ORDERS' = 'REPORT_ORDERS';
const CUSTOMERS: 'CUSTOMERS' = 'CUSTOMERS';
const USERS: 'USERS' = 'USERS';
const CUSTOMER_TAG_TREE: 'CUSTOMER_TAG_TREE' = 'CUSTOMER_TAG_TREE';
const VARIATION_PAIR_OBJECT_MOVIE: 'VARIATION_PAIR_OBJECT_MOVIE' =
  'VARIATION_PAIR_OBJECT_MOVIE';
const ORDER_HISTORY: 'ORDER_HISTORY' = 'ORDER_HISTORY';
const CAMPAIGNS: 'CAMPAIGNS' = 'CAMPAIGNS';
const CAMPAIGN: 'CAMPAIGN' = 'CAMPAIGN';
const CONTACTS: 'CONTACTS' = 'CONTACTS';
const GROUPS: 'GROUPS' = 'GROUPS';
const PRODUCT_SOURCE: 'PRODUCT_SOURCE' = 'PRODUCT_SOURCE';
const ORDERS: 'ORDERS' = 'ORDERS';
const CAMPAIGN_RECIPIENTS: 'CAMPAIGN_RECIPIENTS' = 'CAMPAIGN_RECIPIENTS';
const GROUP: 'GROUP' = 'GROUP';
const TEST_EMAIL: 'TEST_EMAIL' = 'TEST_EMAIL';

export type ResourceName =
  | typeof MANAGED_USER
  | typeof MANAGED_USERS
  | typeof USER
  | typeof PRESIGNED_POSTS
  | typeof PRODUCTS
  | typeof PRODUCT_NOTE
  | typeof PRODUCT_NOTES
  | typeof PRODUCTS_FOR_SCAN
  | typeof BARCODES_FOR_SCAN
  | typeof EXTERNAL_PRICES
  | typeof QUIK_ORDER_LIST
  | typeof QUIK_CONTACT_LIST
  | typeof QUIK_SHIPMENT_LIST
  | typeof QUIK_OPEN_ITEMS_LIST
  | typeof QUIK_ORDER_DETAILS
  | typeof QUIK_PAYMENT_DETAILS
  | typeof QUIK_SHIPMENT_DETAILS
  | typeof QUIK_PAYMENT_LIST
  | typeof QUIK_PENDING_ORDER_LIST
  | typeof RETURN_LABEL
  | typeof REPORT_ORDERS
  | typeof CUSTOMERS
  | typeof USERS
  | typeof CUSTOMER_TAG_TREE
  | typeof VARIATION_PAIR_OBJECT_MOVIE
  | typeof ORDER_HISTORY
  | typeof CAMPAIGNS
  | typeof CAMPAIGN
  | typeof CONTACTS
  | typeof GROUPS
  | typeof CAMPAIGN
  | typeof PRODUCT_SOURCE
  | typeof ORDERS
  | typeof CAMPAIGN_RECIPIENTS
  | typeof GROUP
  | typeof TEST_EMAIL;
// et cetera

export type Resource<T, SerializedT> = {
  name: ResourceName,
  path: string | ((id: string) => string),
  serialized:
    | ((obj: $Shape<T>) => $Shape<SerializedT>)
    | ((arr: Array<$Shape<T>>) => Array<$Shape<SerializedT>>),
  deserialized:
    | ((obj: $Shape<SerializedT>) => $Shape<T>)
    | ((arr: Array<$Shape<SerializedT>>) => Array<$Shape<T>>),
  schema?: Object,
};

export const managedUser: Resource<ManagedUser, SerializedManagedUser> = {
  name: MANAGED_USER,
  path: 'users/managed',
  serialized: serializedManagedUser,
  deserialized: deserializedManagedUser,
};

export const managedUsers: Resource<ManagedUser, SerializedManagedUser> = {
  name: MANAGED_USERS,
  path: 'users/managed',
  serialized: map(serializedManagedUser),
  // $FlowFixMe[incompatible-type]
  deserialized: deserializedManagedUsers,
};

/* ⚠️ need to make a User type */
export const user: Resource<ManagedUser, SerializedManagedUser> = {
  name: USER,
  path: (id) => `users/${id}`,
  serialized: serializedManagedUser,
  deserialized: deserializedManagedUser,
};

export const presignedPost: Resource<PresignedPost, PresignedPost> = {
  name: PRESIGNED_POSTS,
  path: 'presigned_posts',
  serialized: identity,
  deserialized: identity,
  schema: presignedPostSchema,
};

export const products: Resource<
  ProductsQueryResponse,
  SerializedProductsQueryResponse,
> = {
  name: PRODUCTS,
  path: (query: any) => `products/?${productsQueryAsQueryString(query)}`,
  // TODO: currently we never do anything with this
  serialized: (identity: any),
  deserialized: deserializedProductsQueryResponse,
};

export const variations: Resource<
  VariationsQueryResponse,
  SerializedVariationsQueryResponse,
> = {
  name: PRODUCTS,
  path: (query: any) => `products/?${productsQueryAsQueryString(query)}`,
  // TODO: currently we never do anything with this
  serialized: (identity: any),
  deserialized: deserializedVariationsQueryResponse,
  // TODO:
  // it would be great to use this, but there are tons of data issues to figure
  // out and it started to get out of scope.
  // schema: variationsQuerySchema,
};

export const productNotes: Resource<ProductNote, SerializedProductNote> = {
  name: PRODUCT_NOTES,
  /* ⚠️ not sure I like this approach */
  // that's because it's terrible
  path: (query: string) => `notes/?${query}`,
  serialized: map(note.serialized),
  deserialized: map(note.deserialized),
};

export const productNote: Resource<ProductNote, SerializedProductNote> = {
  name: PRODUCT_NOTE,
  path: (id) => `notes/${id}`,
  serialized: note.serialized,
  deserialized: note.deserialized,
};

export const productsForScan: Resource<
  ProductsQueryResponse,
  SerializedProductsQueryResponse,
> = {
  name: PRODUCTS_FOR_SCAN,
  path: ({ scan, customerNumber }: any) => {
    // eslint-disable-next-line babel/no-unused-expressions
    (scan: Scan);
    const keyword = scan.keys ? scan.keys.join(',') : scan.code;
    return `products/?keyword=${keyword}&customer=${customerNumber}&hoist_quantities=true`;
  },
  serialized: (identity: any),
  deserialized: deserializedProductsQueryResponse,
};

// eslint-disable-next-line flowtype/generic-spacing
export const quikOrderList: Resource<
  QuikOrderListQuery,
  SerializedQuikOrderListQuery,
> = {
  name: QUIK_ORDER_LIST,
  // path: () => 'https://quiksilver.staging.elasticsuite.com/api/integrations/order_history/order_list',
  path: () => 'integrations/order_history/order_list',
  // $FlowFixMe[incompatible-type]
  serialized: identity,
  // $FlowFixMe[incompatible-type]
  deserialized: deserializedQuikOrderListResponse,
};

// eslint-disable-next-line flowtype/generic-spacing
export const quikContactList: Resource<
  QuikContactListQuery,
  SerializedQuikContactListQuery,
> = {
  name: QUIK_CONTACT_LIST,
  // path: () => 'https://quiksilver.staging.elasticsuite.com/api/integrations/order_history/contact_list',
  path: () => 'integrations/order_history/contact_list',
  // $FlowFixMe[incompatible-type]
  serialized: identity,
  // $FlowFixMe[incompatible-type]
  deserialized: deserializedQuikContactListResponse,
};

// eslint-disable-next-line flowtype/generic-spacing
export const quikShipmentList: Resource<
  QuikShipmentListQuery,
  SerializedQuikShipmentListQuery,
> = {
  name: QUIK_SHIPMENT_LIST,
  // path: () => 'https://quiksilver.staging.elasticsuite.com/api/integrations/order_history/shipment_list',
  path: () => 'integrations/order_history/shipment_list',
  // $FlowFixMe[incompatible-type]
  serialized: identity,
  // $FlowFixMe[incompatible-type]
  deserialized: deserializedQuikShipmentListResponse,
};

// eslint-disable-next-line flowtype/generic-spacing
export const quikOpenItemsList: Resource<
  QuikOpenItemsListQuery,
  SerializedQuikOpenItemsListQuery,
> = {
  name: QUIK_OPEN_ITEMS_LIST,
  // path: () => 'https://quiksilver.staging.elasticsuite.com/api/integrations/order_history/open_items_list',
  path: () => 'integrations/order_history/open_items_list',
  // $FlowFixMe[incompatible-type]
  serialized: identity,
  // $FlowFixMe[incompatible-type]
  deserialized: deserializedQuikOpenItemsListResponse,
};

// eslint-disable-next-line flowtype/generic-spacing
export const quikOrderDetails: Resource<
  QuikOrderDetailsQuery,
  SerializedQuikOrderDetailsQuery,
> = {
  name: QUIK_ORDER_DETAILS,
  // path: () => 'https://quiksilver.staging.elasticsuite.com/api/integrations/order_history/order_details',
  path: () => 'integrations/order_history/order_details',
  // $FlowFixMe[incompatible-type]
  serialized: identity,
  // $FlowFixMe[incompatible-type]
  deserialized: deserializedQuikOrderDetailsResponse,
};

// eslint-disable-next-line flowtype/generic-spacing
export const quikPaymentDetails: Resource<
  QuikPaymentDetailsQuery,
  SerializedQuikPaymentDetailsQuery,
> = {
  name: QUIK_PAYMENT_DETAILS,
  path: () => 'integrations/order_history/payment_details',
  // $FlowFixMe[incompatible-type]
  serialized: identity,
  // $FlowFixMe[incompatible-type]
  deserialized: deserializedQuikPaymentDetailsResponse,
};

// eslint-disable-next-line flowtype/generic-spacing
export const quikShipmentDetails: Resource<
  QuikShipmentDetailsQuery,
  SerializedQuikShipmentDetailsQuery,
> = {
  name: QUIK_SHIPMENT_DETAILS,
  path: () => 'integrations/order_history/shipment_details',
  // $FlowFixMe[incompatible-type]
  serialized: identity,
  // $FlowFixMe[incompatible-type]
  deserialized: deserializedQuikShipmentDetailsResponse,
};

// eslint-disable-next-line flowtype/generic-spacing
export const quikPaymentList: Resource<
  QuikPaymentListQuery,
  SerializedQuikPaymentListQuery,
> = {
  name: QUIK_PAYMENT_LIST,
  path: () => 'integrations/order_history/payment_list',
  // $FlowFixMe[incompatible-type]
  serialized: identity,
  // $FlowFixMe[incompatible-type]
  deserialized: deserializedQuikPaymentListResponse,
};

export const returnLabel: Resource<ReturnLabel, SerializedReturnLabel> = {
  name: RETURN_LABEL,
  path: 'return_labels',
  serialized: label.serialized,
  // $FlowFixMe[incompatible-type]
  deserialized: label.deserialized,
};

export const campaign: Resource<CampaignDraft, SerializedCampaignDraft> = {
  name: CAMPAIGN,
  path: (id) => `campaigns/${id}`,
  serialized: serializedCampaignDraft,
  // $FlowFixMe[incompatible-type]
  deserialized: identity,
};

export const createCampaign: Resource<CampaignDraft, SerializedCampaignDraft> =
  {
    name: CAMPAIGN,
    path: 'campaigns',
    serialized: serializedCampaignDraft,
    // $FlowFixMe[incompatible-type]
    deserialized: identity,
  };

export const updateCampaign: Resource<CampaignDraft, SerializedCampaignDraft> =
  {
    name: CAMPAIGN,
    path: (id) => `campaigns/${id}`,
    serialized: serializedEditedCampaign,
    // $FlowFixMe[incompatible-type]
    deserialized: identity,
  };

export const reportOrders: Resource<
  ReportOrdersFormValues,
  SerializedReportOrders,
> = {
  name: REPORT_ORDERS,
  path: 'print_jobs',
  serialized: serializeReportOrders,
  // $FlowFixMe[incompatible-type]
  deserialized: deserializeReportOrders,
};

export const barcodesForScan: Resource<BarcodeQuery, SerializedBarcodeQuery> = {
  name: BARCODES_FOR_SCAN,
  path: ({ scan, customerNumber }: any) =>
    `integrations/barcode/scan/?barcodes[]=${
      (scan: Scan).code
    }&customer=${customerNumber}`,
  // $FlowFixMe[incompatible-type]
  serialized: identity,
  deserialized: map(deserializedBarcode),
};

export const externalPrices: Resource<
  ExternalPricesQuery,
  SerializedExternalPricesQuery,
> = {
  name: EXTERNAL_PRICES,
  path: ({
    catalogKey,
    customerNumber,
    products: p,
    stockItemIds,
    variationIds,
    orderType,
  }: any) =>
    [
      `prices/?catalog_key=${catalogKey}`,
      `customer_number=${customerNumber}`,
      `order_type=${orderType}`,
      ...p.map(({ _id }) => `product_ids[]=${_id}`),
      ...variationIds.map((id) => `variation_ids[]=${id}`),
      ...stockItemIds.map((id) => `stock_item_ids[]=${id}`),
    ].join('&'),
  // $FlowFixMe[incompatible-type]
  serialized: identity,
  // $FlowFixMe[speculation-ambiguous]
  deserialized: ({ data, errors }) => ({ data, errors }),
};

// eslint-disable-next-line flowtype/generic-spacing
export const quikPendingOrderList: Resource<
  QuikPendingOrder,
  SerializedQuikPendingOrderListResponse,
> = {
  name: QUIK_PENDING_ORDER_LIST,
  path: ({
    customerNumber,
    poNumber,
    orderNumber,
    createdFrom,
    createdTo,
    startFrom,
    startTo,
  }: any) =>
    [
      'documents/?state=final_submission',
      'pending=true',
      `customer_number=${customerNumber}`,
      orderNumber ? `document_number=${orderNumber}` : null,
      poNumber ? `po_number=${poNumber}` : null,
      createdFrom
        ? `created_on_start=${stringAtStartOfISODay(createdFrom)}`
        : null,
      createdTo ? `created_on_end=${stringAtStartOfISODay(createdTo)}` : null,
      startFrom ? `arrive_on_start=${stringAtStartOfISODay(startFrom)}` : null,
      startTo ? `arrive_on_end=${stringAtStartOfISODay(startTo)}` : null,
    ]
      .filter(Boolean)
      .join('&'),
  serialized: (map(identity): any),
  deserialized: (map(deserializedQuikPendingOrderListResponse): any),
};

export const customers: Resource<
  CustomersQueryResponse,
  SerializedCustomersQueryResponse,
> = {
  name: CUSTOMERS,
  path: (query: any) => `customers/?${customersQueryAsQueryString(query)}`,
  serialized: (identity: any),
  deserialized: deserializedCustomersQueryResponse,
};

export const users: Resource<UsersQueryResponse, SerializedUsersQueryResponse> =
  {
    name: USERS,
    path: (query: any) => `/users/?${usersQueryAsQueryString(query)}`,
    serialized: (identity: any),
    deserialized: deserializedUsersQueryResponse,
  };

export const customerTagTree: Resource<
  CustomerTagTreeResponse,
  CustomerTagTreeResponse,
> = {
  name: CUSTOMER_TAG_TREE,
  path: (id) => `/customers/${id}/tag_tree`,
  serialized: (identity: any),
  deserialized: (identity: any),
};

export const orders: Resource<OrdersQueryResponse, OrdersQueryResponse> = {
  name: ORDERS,
  path: (query: any) =>
    `documents/?${documentsQueryAsQueryString((query: DocumentsQuery))}`,
  serialized: (identity: any),
  deserialized: (identity: any),
};

export const variationsForCampaigns: Resource<
  VariationsQueryResponseForCampaigns,
  VariationsQueryResponseForCampaigns,
> = {
  name: PRODUCTS,
  path: (query: any) =>
    `products/?${variationsForCampaignsQueryAsQueryString(query)}`,
  serialized: (identity: any),
  deserialized: (identity: any),
};

// eslint-disable-next-line flowtype/generic-spacing
export const variationPairObjectMovie: Resource<
  ObjectMovieQueryResponse,
  ObjectMovieQueryResponse,
> = {
  name: VARIATION_PAIR_OBJECT_MOVIE,
  path: ({ productNumber, variationCode }: any) =>
    `/products/object_movies?number=${productNumber}&variation_code=${variationCode}`,
  serialized: (identity: any),
  deserialized: (identity: any),
};

// eslint-disable-next-line flowtype/generic-spacing
export const orderHistory: Resource<
  OrderHistoryResponse,
  SerializedOrderHistoryResponse,
> = {
  name: ORDER_HISTORY,
  path: (query: any) =>
    `order_history/?${orderHistoryQueryAsString((query: OrderHistoryQuery))}`,
  serialized: (identity: any),
  deserialized: deserializedOrderHistoryResponse,
};

export const campaigns: Resource<
  CampaignsQueryResponse,
  SerializedCampaignsQueryResponse,
> = {
  name: CAMPAIGNS,
  path: (query: any) =>
    `campaigns?${campaignsQueryAsQueryString((query: CampaignsQuery))}`,
  serialized: (identity: any),
  deserialized: deserializedCampaignsQueryResponse,
};

export const campaignRecipients: Resource<
  CampaignRecipientsQueryResponse,
  SerializedCampaignRecipientsQueryResponse,
> = {
  name: CAMPAIGN_RECIPIENTS,
  path: (query: any) =>
    `campaigns/${
      query.id
    }/recipients?${campaignRecipientsQueryQueryAsQueryString(
      (query: CampaignRecipientsQuery),
    )}`,
  serialized: (identity: any),
  deserialized: deserializedCampaignRecipientsQueryResponse,
};

export const contacts: Resource<
  ContactsQueryResponse,
  SerializedContactsQueryResponse,
> = {
  name: CONTACTS,
  path: (query: any) =>
    `campaigns/contacts?${contactsQueryAsQueryString((query: ContactsQuery))}`,
  serialized: (identity: any),
  deserialized: deserializedContactsQueryResponse,
};

export const groups: Resource<
  GroupsQueryResponse,
  SerializedGroupsQueryResponse,
> = {
  name: GROUPS,
  path: (query: any) =>
    `campaigns/groups?${groupsQueryAsQueryString((query: GroupsQuery))}`,
  serialized: (identity: any),
  deserialized: deserializedGroupsQueryResponse,
};

export const group: Resource<GroupQueryResponse, GroupQueryResponse> = {
  name: GROUP,
  path: (id) => `campaigns/groups/${id}`,
  serialized: (identity: any),
  deserialized: (identity: any),
};

export const testEmail: Resource<
  CampaignSendTestEmailResponse,
  SerializedCampaignSendTestEmailResponse,
> = {
  name: TEST_EMAIL,
  path: (query: any) =>
    `campaigns/${query.id}/test_send?${campaignSendTestEmailQueryAsQueryString(
      (query: CampaignSendTestEmailQuery),
    )}`,
  serialized: (identity: any),
  deserialized: (identity: any),
};
